<!--This Component is used for OAP step when the vendor is Sepio and AFP does not have a Sepio Guard API Number-->
<template>
	<div class="flex h-full flex-col items-center gap-4 px-40 py-20 text-center">
		<ExclamationTriangleIcon class="size-12 text-yellow-400" />
		<p class="text-2 font-bold">
			{{ t('document.sepio.noProducerFormTitle') }}
		</p>
		<p class="text-sm font-normal text-slate-500">
			{{ t('document.sepio.noProducerFormMessage') }}
		</p>

		<!-- Offline form to enter Sepio Membership Number -->
		<div class="my-4">
			<p class="flex items-center gap-1 text-lg font-semibold italic">
				{{ t('document.sepio.noProducerFormPrompt') }}
				<button
					:aria-label="t('document.offlineFormToggleLabel')"
					type="button"
					class="flex gap-1"
					@click="toggleForm"
				>
					<span class="text-primary-500 italic underline">
						{{ t('oap.sepioAppLink') }}
					</span>
					<ChevronRightIcon
						class="text-primary-500 h-6 w-6 text-xs"
						stroke-width="2"
						:class="{ 'rotate-90 transform': showForm }"
					/>
				</button>
			</p>
			<div
				v-if="showForm"
				class="w-full pb-8"
				:aria-label="t('document.offlineFormContainerLabel')"
			>
				<FormKit
					type="text"
					name="sepioMembershipNumber"
					:label="t('oap.sepioMembershipNumber')"
					outer-class="max-w-full! mt-9"
					input-class="h-8"
					inner-class="border! border-slate-300! rounded-lg"
					label-class="mb-2"
					validation="required"
					v-model="sepioMembershipNumber"
				/>
				<Teleport to="#oapEnrollmentNext">
					<button
						type="button"
						class="text-accent-700! h-12 w-60 justify-center self-end rounded-md border-0 font-extrabold transition-all duration-300"
						:class="
							valid
								? 'bg-accent-400! hover:bg-accent-500! hover:text-accent-800!'
								: 'bg-secondary-300! hover:bg-secondary-300! cursor-not-allowed text-slate-500/50! hover:text-slate-500/50!'
						"
						@click="handleNext"
					>
						{{ t('document.next') }}
					</button>
				</Teleport>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import type Plan from '@/classes/Plan'
import { PlanOapPaymentType } from '@/enums/PlanOapPaymentType'
import { usePresentationLocale } from '@/i18n'
import { usePlanStore } from '@/stores/plan'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type { SepioOapPayment } from '@/types'
import { ChevronRightIcon } from '@heroicons/vue/24/outline'
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid'
import { computed, nextTick, ref } from 'vue'

const sessionStore = useSessionStore()
const sessionDocumentsStore = useSessionDocumentsStore()
const planStore = usePlanStore()
const { t } = usePresentationLocale()

const showForm = ref(true)
const sepioMembershipNumber = ref('')

const proposedInsured = computed(
	() =>
		sessionStore.getPlanHolder(
			sessionDocumentsStore.currentPlanHolderID
		) as Plan
)

const valid = computed(() => sepioMembershipNumber.value?.length > 0)

const toggleForm = async () => {
	showForm.value = !showForm.value
	if (showForm.value) {
		await nextTick()

		document.querySelector('#step-content-container')?.scrollTo({
			top: document.querySelector('#step-content-container')?.scrollHeight,
		})
	}
}

const handleNext = async () => {
	const { paymentPlanId, provider } = proposedInsured.value
		.oapPayment as SepioOapPayment

	await planStore.updatePlan(sessionDocumentsStore.currentPlanHolderID, {
		oapPayment: {
			paymentPlanId,
			provider,
			paymentPlan: {
				paymentType: PlanOapPaymentType.CreditCard,
				paymentDetails: { sepioMembershipNumber: sepioMembershipNumber.value },
			},
		},
	})

	await sessionDocumentsStore.setOapStepTo('document')
}
</script>
